import { useEffect, useRef, useState } from 'react'

export const SESSION_MAX_AGE_MS = 48 * 60 * 60 * 1000 // 48 hours

/**
 * Tracks whether the current session has outlived SESSION_MAX_AGE_MS.
 *
 * A long-running `setTimeout` would be throttled or discarded while the tab
 * sits in the background, so instead the load timestamp is stored and
 * compared whenever the window regains focus or the tab becomes visible
 * again. Sticky once true.
 */
export function useSessionExpired(): boolean {
  const [expired, setExpired] = useState(false)
  const loadedAtRef = useRef(Date.now())

  useEffect(() => {
    if (expired) return

    function checkAge(): void {
      if (Date.now() - loadedAtRef.current >= SESSION_MAX_AGE_MS) {
        setExpired(true)
      }
    }

    function handleVisibilityChange(): void {
      if (document.visibilityState === 'visible') checkAge()
    }

    window.addEventListener('focus', checkAge)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return (): void => {
      window.removeEventListener('focus', checkAge)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [expired])

  return expired
}
